x<template>
  <Head :title="__('booking-vips.meta.title')">
    <meta
      name="description"
      :content="__('booking-vips.meta.description')"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.booking.index')"
    >
    <meta
      property="og:image"
      content="/frontend/assets/favicon/og-image.jpg"
    >
  </Head>

  <section class="all-vips-hero py-8 relative">
    <template v-if="$page.props.darkModeEnabled">
      <img
        src="/frontend/assets/mobile-hero-swirl-white.svg"
        class="absolute top-0 left-0 lg:hidden"
      >
      <img
        src="/frontend/assets/desktop-hero-swirl-white.svg"
        class="absolute top-0 left-0 hidden lg:block"
      >
    </template>
    <template v-else>
      <img
        src="/frontend/assets/mobile-hero-swirl.svg"
        class="absolute top-0 left-0 lg:hidden"
      >
      <img
        src="/frontend/assets/desktop-hero-swirl.svg"
        class="absolute top-0 left-0 hidden lg:block"
      >
    </template>

    <div class="container">
      <div class="text-center px-8">
        <h1 class="text-5xl font-bold lg:text-7xl dark:text-white">
          {{ __('booking-vips.title') }}
        </h1>

        <p class="text-gray-400 dark:text-white mt-2 mx-auto lg:text-xl max-w-4xl">
          <i18n-t
            keypath="booking-vips.text"
            scope="global"
          >
            <template #link>
              <Link
                :href="localeRoute('frontend.index')"
                class="underline hover:no-underline"
              >
                {{ __('booking-vips.text-link') }}
              </Link>
            </template>
          </i18n-t>
        </p>
      </div>
    </div>

    <form
      class="w-full absolute px-[15px] md:left-1/2 md:transform md:-translate-x-1/2 md:w-[450px] lg:w-[550px]"
      @submit.prevent="$refs.vipsFilter.submitFilter"
    >
      <div class="relative">
        <img
          src="/frontend/assets/input-zoom.svg"
          class="absolute left-[16px] top-[15px] lg:top-[18px] lg:left-[20px]"
        >

        <input
          v-model="search"
          type="search"
          class="w-full py-[13px] text-lg rounded-xl shadow-input placeholder-gray-250 border-red border-2 focus:ring-red-900 focus:ring-offset-red-900 focus:border-red-900 pl-12 lg:py-4"
          :placeholder="__('vips.search.placeholder')"
          maxlength="100"
        >
      </div>
    </form>
  </section>

  <section class="all-vips bg-white dark:bg-gray-900 py-12 overflow-hidden">
    <BookingVipsFilter
      ref="vipsFilter"
      v-model:search="search"
      :total-vips="filteredVipsCount"
      :occasions="occasions"
      :categories="categories"
      :initial-filter="initialFilter"
      @filter:reset="resetFilter"
      @filter:recalculate="recalculateFilter"
      @filter:submit="submitFilter"
    />

    <div v-show="filteredVips.length > 0">
      <div
        v-for="(vipsGroup, i) in groupedVips"
        :key="`vip-group-${i}`"
      >
        <div class="container">
          <div class="w-full md:order-3">
            <div class="w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-8 my-4">
              <BookingVip
                v-for="vip in vipsGroup"
                :key="`vip-${vip.username}-${vip.id}`"
                :vip="vip"
                :always-mobile="true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="filteredVips.length === 0">
      <section class="all-vips-no-results bg-white dark:bg-gray-900 pb-20 overflow-hidden">
        <div class="container">
          <div class="mt-4 text-center">
            <span class="block text-center text-2xl lg:text-5xl dark:text-white">
              <i18n-t
                keypath="vips.nothing-found"
                scope="global"
              >
                <template #count>
                  <strong>0</strong>
                </template>
              </i18n-t>
            </span>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import debounce from 'lodash/debounce';
import BookingVip from '../../Components/BookingVip.vue';
import BookingVipsFilter from '../../Components/BookingVipsFilter.vue';

export default {
  components: {
    BookingVip,
    BookingVipsFilter,
  },

  props: {
    vips: {
      type: Array,
      required: true,
    },

    initialSearch: {
      type: String,
      required: false,
      default: '',
    },

    occasions: {
      type: Array,
      required: true,
    },

    categories: {
      type: Array,
      required: true,
    },

    initialFilter: {
      type: Object,
      required: false,
      default: () => {},
    },
  },

  data() {
    return {
      filteredVips: this.vips,
      filteredVipsCount: this.vips.length,

      search: this.initialSearch || '',
      page: 1,
    };
  },

  computed: {
    groupedVips() {
      return this.filteredVips
        .reduce((accumulator, vip, index) => {
          if (index % 12) {
            accumulator[accumulator.length - 1].push(vip);
          } else {
            accumulator.push([vip]);
          }

          return accumulator;
        }, []);
    },
  },

  watch: {
    search() {
      this.$nextTick(() => {
        this.$refs.vipsFilter.submitFilter();
      });
    },
  },

  created() {
    this.submitFilter = debounce(this.submitFilter, 100);
  },

  mounted() {
    this.$refs.vipsFilter.submitFilter(false, true);
  },

  methods: {
    shouldDisplayVipsOccasions(currentIndex) {
      return currentIndex === 1 && this.groupedVips[currentIndex + 1]?.length > 0;
    },

    shouldDisplayVipsCategories(currentIndex) {
      return currentIndex === 0 && this.groupedVips[currentIndex + 1]?.length > 0;
    },

    resetFilter() {
      this.search = '';

      this.submitFilter();
    },

    recalculateFilter(filter) {
      const filteredVips = this.vips
        .filter((vip) => {
          if (!('search' in filter)) {
            return true;
          }

          const inDisplayName = vip.content.display_name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .includes(
              filter.search
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase(),
            );
          const inAbout = vip.content.about
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/<\/?[^>]+>/ig, ' ')
            .toLowerCase()
            .includes(
              filter.search
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase(),
            );
          return inAbout || inDisplayName;
        })
        .filter((vip) => {
          if (!('occasion' in filter)) {
            return true;
          }

          return Object.keys(vip.content.occasions).find((key) => vip.content.occasions[key].slug === filter.occasion);
        })
        .filter((vip) => {
          if (!('category' in filter)) {
            return true;
          }

          return Object.keys(vip.content.categories).find((key) => vip.content.categories[key].slug === filter.category);
        })
        .filter((vip) => {
          if (!('price_min' in filter)) {
            return true;
          }

          return vip.content.price >= filter.price_min * 100;
        })
        .filter((vip) => {
          if (!('price_max' in filter)) {
            return true;
          }

          return vip.content.price <= filter.price_max * 100;
        })
        .filter((vip) => {
          if (!('sort' in filter) || filter.sort !== 'within_2_days') {
            return true;
          }

          return vip.content.offers_48h === true;
        })
        .sort((a, b) => {
          switch (filter.sort) {
            case 'popular':
              return a.shout_outs_count > b.shout_outs_count ? -1 : 1;
            case 'price':
              return a.content.price - b.content.price;
            case 'newest':
              return b.created_timestamp - a.created_timestamp;
            case 'rating':
              return b.average_rating - a.average_rating;
            case 'speed':
              return a.average_delivery_time < b.average_delivery_time ? -1 : 1;
            default:
              return;
          }
        });

      this.filteredVipsCount = filteredVips.length;

      return filteredVips;
    },

    submitFilter(filter = {}, initial = false) {
      this.filteredVips = this.recalculateFilter(filter);

      if (!initial) {
        this.$inertia.get(this.localeRoute('frontend.booking.index'),filter, {
          replace: false,
          preserveState: true,
          preserveScroll: true,
        });
      }
    },
  },

};
</script>
